import React, { useState, useRef } from 'react';
import Modal from './Modal';
import Badge from './Badge';
import Button from './Button';
import { Image, AlertTriangle, Copy, Sparkles, ExternalLink, ChevronDown, ChevronRight, Wand2 } from 'lucide-react';
import { generateAltSuggestions } from '../api';

/**
 * Generates a pertinent alt text suggestion using the image context, filename, and parent tag
 */
function generateAltSuggestion(img) {
    // 1. Best: use context text to generate a meaningful alt
    if (img.context_text && img.context_text.trim().length > 10) {
        const context = img.context_text.trim();
        // Clean up the context: take the first sentence or meaningful fragment
        const firstSentence = context.split(/[.!?]\s/)[0];
        const cleanText = firstSentence.slice(0, 100).trim();

        // Determine image role from parent tag
        const tag = (img.context_tag || '').toLowerCase();
        if (tag === 'a' || tag === 'nav') {
            return `Lien vers : ${cleanText}`;
        }
        if (tag === 'header' || tag === 'banner') {
            return `Bannière : ${cleanText}`;
        }
        if (tag === 'figure' || tag === 'picture') {
            return `Illustration : ${cleanText}`;
        }
        if (tag === 'li' || tag === 'ul' || tag === 'ol') {
            return `Icône pour : ${cleanText}`;
        }

        // Generic context-based suggestion
        return `Image illustrant ${cleanText}`;
    }

    // 2. Fallback: extract from filename
    try {
        const url = new URL(img.src);
        const filename = url.pathname.split('/').pop() || '';
        let name = filename
            .replace(/\.[^.]+$/, '')
            .replace(/[-_]+/g, ' ')
            .replace(/\b\d{4,}\b/g, '')
            .replace(/\b(img|image|photo|pic|banner|hero|bg|background|icon|thumb|thumbnail)\b/gi, '')
            .replace(/\s+/g, ' ')
            .trim();
        if (name.length > 3) {
            return name.charAt(0).toUpperCase() + name.slice(1);
        }
    } catch { }

    // 3. Last resort
    return 'Image descriptive du contenu de la page';
}

function ImageCard({ img, index, onCopy, altOverride, isGenerating }) {
    const [imgError, setImgError] = useState(false);
    const [showContext, setShowContext] = useState(false);
    const hasGeneratedAlt = altOverride != null && altOverride !== '';

    return (
        <div className="border border-blue-gray-200 rounded-xl p-4 hover:border-accent/40 transition-colors bg-white">
            <div className="flex gap-4">
                {/* Preview */}
                <div className="flex-shrink-0 w-20 h-20 rounded-lg bg-blue-gray-50 border border-blue-gray-100 flex items-center justify-center overflow-hidden">
                    {!imgError && img.src && !img.src.startsWith('data:') ? (
                        <img
                            src={img.src}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={() => setImgError(true)}
                            loading="lazy"
                        />
                    ) : (
                        <Image size={24} className="text-blue-gray-300" />
                    )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-blue-gray-400">#{index + 1}</span>
                        <Badge color="error" className="text-xs">Alt manquant</Badge>
                        {img.context_tag && (
                            <span className="text-xs text-blue-gray-400 font-mono">&lt;{img.context_tag}&gt;</span>
                        )}
                    </div>

                    {/* Source URL — compact */}
                    <div className="text-xs text-blue-gray-500 font-mono truncate mb-2" title={img.src}>
                        {img.src ? (
                            <a href={img.src} target="_blank" rel="noopener noreferrer" className="hover:text-accent flex items-center gap-1">
                                {img.src.replace(/^https?:\/\//, '').slice(0, 50)}
                                {img.src.length > 50 ? '…' : ''}
                                <ExternalLink size={10} className="flex-shrink-0" />
                            </a>
                        ) : (
                            <span className="italic text-blue-gray-300">Source inconnue</span>
                        )}
                    </div>

                    {/* Bloc vert : uniquement si une suggestion IA a été générée */}
                    {hasGeneratedAlt ? (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-2.5 flex items-start gap-2">
                            <Sparkles size={14} className="text-green-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-green-700 mb-0.5">Suggestion d'alt :</div>
                                <div className="text-sm text-green-800 font-medium">{altOverride}</div>
                            </div>
                            <button
                                onClick={() => onCopy(altOverride)}
                                className="p-1 rounded text-green-600 hover:bg-green-100 transition-colors flex-shrink-0"
                                title="Copier la suggestion"
                            >
                                <Copy size={14} />
                            </button>
                        </div>
                    ) : isGenerating ? (
                        <div className="bg-blue-gray-50/50 border border-blue-gray-100 rounded-lg px-2.5 py-2.5 flex items-start gap-2">
                            <div className="h-3.5 w-3.5 rounded bg-blue-gray-200 animate-pulse flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0 space-y-2">
                                <div className="h-3 w-16 rounded bg-blue-gray-200 animate-pulse" />
                                <div className="h-3 w-full max-w-[220px] rounded bg-blue-gray-100 animate-pulse" />
                            </div>
                        </div>
                    ) : (
                        <div className="text-xs text-blue-gray-400 bg-blue-gray-50/50 border border-blue-gray-100 rounded-lg px-2.5 py-2">
                            Suggestion d'alt : générez avec le bouton « Générer les alt avec l'IA » en haut.
                        </div>
                    )}

                    {/* Context — dropdown */}
                    {img.context_text && (
                        <button
                            onClick={() => setShowContext(!showContext)}
                            className="mt-2 text-xs text-blue-gray-400 hover:text-blue-gray-600 flex items-center gap-1 transition-colors"
                        >
                            {showContext ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            Contexte sur la page
                        </button>
                    )}
                    {showContext && img.context_text && (
                        <div className="mt-1.5 text-xs text-blue-gray-500 bg-blue-gray-50 rounded-lg p-2.5 border border-blue-gray-100">
                            {img.context_text}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const REVEAL_DELAY_MS = 80;

export default function ImagesWithoutAltModal({ open, onClose, images = [], totalImages = 0, onCopy, slug, pageUrl, onError }) {
    const [geminiAlts, setGeminiAlts] = useState({});
    const [generateLoading, setGenerateLoading] = useState(false);
    const revealRunRef = useRef(0);
    const revealTimeoutsRef = useRef([]);

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        onCopy?.(text);
    };

    const handleGenerateWithGemini = async () => {
        if (!slug || !pageUrl || !images.length) {
            onError?.('Données manquantes (projet ou URL de la page).');
            return;
        }
        revealTimeoutsRef.current.forEach(clearTimeout);
        revealTimeoutsRef.current = [];
        revealRunRef.current += 1;
        const currentRun = revealRunRef.current;
        setGenerateLoading(true);
        setGeminiAlts({});
        try {
            const { suggestions } = await generateAltSuggestions(slug, pageUrl, images);
            const list = (suggestions || []).filter((s) => s.src && s.alt != null && s.alt !== '');
            setGenerateLoading(false);

            if (currentRun !== revealRunRef.current) return;
            if (list.length === 0) return;

            // Afficher les suggestions une par une pour donner une impression de progression
            list.forEach((s, i) => {
                const t = setTimeout(() => {
                    if (currentRun !== revealRunRef.current) return;
                    setGeminiAlts((prev) => ({ ...prev, [s.src]: s.alt }));
                }, i * REVEAL_DELAY_MS);
                revealTimeoutsRef.current.push(t);
            });
        } catch (err) {
            setGenerateLoading(false);
            const msg = err?.response?.data?.detail || err?.message || 'Erreur lors de la génération des alt.';
            onError?.(msg);
        }
    };

    const hasGenerated = Object.keys(geminiAlts).length > 0;
    const canGenerate = images.length > 0 && slug && pageUrl;

    return (
        <Modal open={open} onClose={onClose} title="Images sans attribut alt" size="xl">
            {/* Header user-friendly : résumé + action claire */}
            <div className="mb-6 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-blue-gray-50 rounded-xl border border-blue-gray-100">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                            <AlertTriangle size={20} className="text-amber-600" />
                        </div>
                        <div>
                            <p className="font-medium text-blue-gray-900">
                                {images.length} image{images.length > 1 ? 's' : ''} sans alt sur {totalImages} au total
                            </p>
                            <p className="text-sm text-blue-gray-600 mt-0.5">
                                L'attribut <strong>alt</strong> décrit l'image pour l'accessibilité et le SEO. Vous pouvez faire générer des suggestions par l'IA ci-dessous.
                            </p>
                        </div>
                    </div>
                    {canGenerate && (
                        <div className="flex flex-col items-stretch sm:items-end gap-2 flex-shrink-0">
                            <Button
                                type="button"
                                variant="primary"
                                onClick={handleGenerateWithGemini}
                                disabled={generateLoading}
                                className="flex items-center justify-center gap-2 w-full sm:w-auto"
                            >
                                <Wand2 size={18} className={generateLoading ? 'animate-pulse' : ''} />
                                {generateLoading ? 'Génération en cours…' : 'Générer les alt avec l\'IA'}
                            </Button>
                            {hasGenerated && (
                                <span className="text-xs text-green-600 font-medium text-center sm:text-right">
                                    ✓ {Object.keys(geminiAlts).length} suggestion{Object.keys(geminiAlts).length > 1 ? 's' : ''} générée{Object.keys(geminiAlts).length > 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Image list */}
            {images.length > 0 ? (
                <div className="space-y-3">
                    {images.map((img, i) => (
                        <ImageCard
                            key={i}
                            img={img}
                            index={i}
                            onCopy={handleCopy}
                            altOverride={geminiAlts[img.src]}
                            isGenerating={generateLoading}
                        />
                    ))}
                </div>
            ) : (
                <div className="text-center py-8 text-blue-gray-400">
                    Aucune image sans attribut alt détectée.
                </div>
            )}

            {/* Code example */}
            <div className="mt-5 p-4 bg-blue-gray-50 rounded-xl">
                <div className="text-xs font-semibold text-blue-gray-600 mb-2">💡 Comment ajouter un attribut alt :</div>
                <code className="text-xs text-blue-gray-700 font-mono block bg-white rounded-lg p-3 border border-blue-gray-200">
                    &lt;img src="image.jpg" <span className="text-green-600 font-bold">alt="Description de l'image"</span> /&gt;
                </code>
            </div>
        </Modal>
    );
}
